/** Reviewed Phase 1D snapshot of the owner's Cheese Prices Tracker, USD per pound.
 * These game balance inputs deliberately do not change when the market runner runs.
 */
export const PRICING_SOURCE = 'cheese-prices-tracker';
export const PRICING_REVISION = 'phase-1d-2026-09-18';
export const PRICING_SOURCE_URL = 'https://docs.google.com/spreadsheets/d/1899avEB5SbSSEKCvgwn_arjnlOuXsC6rTBzr0UNvWKo/edit#gid=0';

/** A common packaging unit; total milk-cheese output is determined by milk and yield. */
export const STANDARD_UNIT_POUNDS = 1;

export interface CheesePriceBand {
  readonly recipeId: string;
  readonly marketKey: string;
  readonly floorPricePerLb: number;
  readonly ceilingPricePerLb: number;
  readonly source: typeof PRICING_SOURCE;
  readonly sourceRevision: typeof PRICING_REVISION;
}

const referenceRows: ReadonlyArray<readonly [string, string, number, number]> = [
  ['mozzarella_fresh', 'mozzarella_fresh', 3.45, 9.88],
  ['ricotta', 'ricotta', 2.24, 3.86],
  ['gouda', 'gouda', 10, 30.50],
  ['havarti', 'havarti', 10, 16],
  ['cheddar', 'cheddar_farmstead', 5.68, 10],
  ['taleggio', 'taleggio', 11.50, 42.09],
  ['parmesan', 'parmesan', 28.98, 32.98],
  ['pecorino_romano', 'pecorino_romano', 15.99, 36.54],
  ['emmental', 'emmental', 10, 30],
  ['gorgonzola', 'gorgonzola', 9.99, 19.99],
  ['roquefort', 'roquefort', 31.98, 35],
];

export function validatePriceBand(band: CheesePriceBand): void {
  if (!band.recipeId || !band.marketKey || !Number.isFinite(band.floorPricePerLb)
    || !Number.isFinite(band.ceilingPricePerLb) || band.floorPricePerLb <= 0
    || band.ceilingPricePerLb < band.floorPricePerLb) {
    throw new Error(`Invalid price band for ${band.recipeId || '(missing recipe)'}`);
  }
}

export const CHEESE_PRICE_BANDS: Readonly<Record<string, CheesePriceBand>> = Object.freeze(
  Object.fromEntries(referenceRows.map(([recipeId, marketKey, floorPricePerLb, ceilingPricePerLb]) => {
    const band = Object.freeze({ recipeId, marketKey, floorPricePerLb, ceilingPricePerLb, source: PRICING_SOURCE, sourceRevision: PRICING_REVISION });
    validatePriceBand(band);
    return [recipeId, band];
  })),
);

export const PRICE_BANDS_BY_MARKET_KEY: Readonly<Record<string, CheesePriceBand>> = Object.freeze(
  Object.fromEntries(Object.values(CHEESE_PRICE_BANDS).map((band) => [band.marketKey, band])),
);

/** Use the finished integer rating. Exactly 2.50 rounds up to three stars. */
export function finalStars(quality: number): number {
  if (!Number.isFinite(quality)) throw new Error('Cheese quality must be finite');
  return Math.min(5, Math.max(1, Math.round(quality)));
}

export function pricePerPound(band: CheesePriceBand, stars: number): number {
  validatePriceBand(band);
  return band.floorPricePerLb + (finalStars(stars) - 1) / 4 * (band.ceilingPricePerLb - band.floorPricePerLb);
}

/** Carry fractional cents forward so selling one at a time cannot manufacture gold.
 * Persist the remainder with the wallet and apply this only once when a sale commits.
 */
export function settleSaleAmount(rawTotal: number, remainder = 0): { amount: number; remainder: number } {
  if (!Number.isFinite(rawTotal) || rawTotal < 0 || !Number.isFinite(remainder) || Math.abs(remainder) > 0.00500001) {
    throw new Error('Invalid sale total or currency remainder');
  }
  const exact = rawTotal + remainder;
  const amount = Math.round((exact + Number.EPSILON * Math.max(1, exact)) * 100) / 100;
  return { amount, remainder: exact - amount };
}
